import { useState, useEffect } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Download, ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';
import { FloatingPaths } from '../BackgroundPaths';
import { toast } from 'sonner';
import ReactConfetti from 'react-confetti';

interface LeadFormData {
  nome: string;
  email: string;
  company: string;
  firstName: string;
  lastName: string;
  cargo?: string;
  areaTrabalho?: string;
  areaEmpresa?: string;
  interesse?: string;
  wantsTrendReport?: boolean;
}

const CARGOS = [
  'Trainee',
  'Estagiário',
  'Assistente / Auxiliar',
  'Analista',
  'Especialista',
  'Supervisor/Coordenador',
  'Gerente',
  'Diretor',
  'C-Level',
  'Fundador',
  'Outro'
];

const AREAS_TRABALHO = [
  'Agência/consultoria',
  'Marca/empresa',
  'Outro'
];

const AREAS_EMPRESA = [
  'Mídia',
  'Growth/perfomance',
  'Marketing',
  'Branding',
  'PR/Comunicação',
  'Consumer Insight',
  'Comercial',
  'Fundador',
  'Outro'
];

const INTERESS = [
  'Sim',
  'Não'
];

// // Adicionando as cores do brandbook
// const BRAND_COLORS = {
//   primary: '#FFD100', // amarelo
//   secondary: '#1D1D1B', // preto
//   tertiary: '#4D4D4D', // cinza
//   background: '#FFFFFF' // branco
// };

const AVAILABLE_CASES = [
  {
    id: 'summerxwaffle',
    title: "I'ts Summer at waffle.",
    description: 'o que nossos leitores pensam sobre o verão?',
    thumbnail: 'https://pub-d4d36832efd647f5a00ae2d9086a65ea.r2.dev/Screenshot%202025-02-21%20at%2010.12.06%E2%80%AFPM.png',
    type: 'pdf'
  },
  {
    id: 'webinar',
    title: "Por que as marcas não conseguem se comunicar com a gen z?",
    description: 'webinar - 19/03/2025 11:00am - 12:00pm',
    thumbnail: 'https://pub-d4d36832efd647f5a00ae2d9086a65ea.r2.dev/banner_webinar_720.png',
    type: 'webinar'
  },
  {
    id: 'thenews_solutions_B2B',
    title: "soluções B2B x the news",
    description: 'como vender soluções B2B para quem realmente decide',
    thumbnail: 'https://pub-d4d36832efd647f5a00ae2d9086a65ea.r2.dev/thenews_solutions_B2B.jpg',
    type: 'pdf'
  },
  {
    id: 'travel_thenews',
    title: "travel x the news",
    description: 'como vender soluções B2C para quem realmente decide',
    thumbnail: 'https://pub-d4d36832efd647f5a00ae2d9086a65ea.r2.dev/travel_thenews.jpg',
    type: 'pdf'
  },
  {
    id: 'commercial',
    title: "propagandas ainda funcionam?",
    description: 'webinar - 25/06/2025 11:00am',
    thumbnail: 'https://assets.testeswaffle.org/propaganda.png',
    type: 'webinar'
  },
];

export default function LeadForm() {
  const [selectedCase, setSelectedCase] = useState<string | null>(null);
  const [formData, setFormData] = useState<LeadFormData>({
    nome: '',
    email: '',
    company: '',
    firstName: '',
    lastName: '',
    cargo: '',
    areaTrabalho: '',
    areaEmpresa: '',
    interesse: '',
    wantsTrendReport: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [filteredCases, setFilteredCases] = useState(AVAILABLE_CASES);
  const [pageTitle, setPageTitle] = useState("cases que inspiram");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const selectParam = params.get('select');

    if (selectParam) {
      const filtered = AVAILABLE_CASES.filter(caseItem => caseItem.id === selectParam);
      setFilteredCases(filtered);

      if (selectParam === 'webinar') {
        setPageTitle("webinars que inspiram");
      }
    } else {
      setFilteredCases(AVAILABLE_CASES);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const fullName = `${formData.firstName.trim()} ${formData.lastName.trim()}`;
    const selectedCaseData = AVAILABLE_CASES.find(c => c.id === selectedCase);

    const payload = {
      nome: fullName,
      email: formData.email,
      company: formData.company,
      file: selectedCase,
      interesse: formData.interesse,
      ...(selectedCaseData?.type === 'webinar' && {
        cargo: formData.cargo,
        areaTrabalho: formData.areaTrabalho,
        areaEmpresa: formData.areaEmpresa
      })
    };

    console.log('Enviando dados:', payload);

    try {
      const response = await fetch('https://temp.testeswaffle.org/webhooks/lead', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      console.log('Status da resposta:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Erro detalhado:', errorText);
        throw new Error(`Erro ao enviar dados: ${response.status} - ${errorText}`);
      }

      if (formData.wantsTrendReport) {
        try {
          await fetch('https://backend.testeswaffle.org/subscribe/newsletters', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              email: formData.email,
              newsletter: 'trendreport'
            })
          });
        } catch (newsletterError) {
          console.error('Erro ao inscrever na newsletter:', newsletterError);
        }
      }

      const result = await response.json();
      console.log('Resposta da API:', result);

      if (result.success) {
        if (selectedCaseData?.type === 'pdf' && result.data.fileUrl) {
          try {
            const fileResponse = await fetch(result.data.fileUrl);
            if (!fileResponse.ok) throw new Error('Erro ao baixar o arquivo');

            const blob = await fileResponse.blob();
            const url = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = url;
            link.download = `${selectedCaseData?.title || 'Case'}.pdf`;
            document.body.appendChild(link);
            link.click();

            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
          } catch (downloadError) {
            throw new Error('Erro ao baixar o arquivo');
          }
        } else if (selectedCaseData?.type === 'webinar') {
          setShowConfetti(true);
          toast.success('Inscrição realizada com sucesso! Você será redirecionado para adicionar o evento ao seu calendário.');
          // setTimeout(() => {
          //   window.location.href = 'https://calendar.google.com/calendar/event?action=TEMPLATE&tmeid=Nmhxb2E1cnQyZG41OWh0bmVtZnA0aXVzYWggY19kY2JiN2Y4MjAxMDViY2Q5YzNjYTBmZWJlYzE0NGIzNmI4ODRmYzY0YTUzYjA0YzI3MzhlMGU0ZTVhOWIxZmVhQGc&tmsrc=c_dcbb7f820105bcd9c3ca0febec144b36b884fc64a53b04c2738e0e4e5a9b1fea%40group.calendar.google.com';
          // }, 2000);
          setTimeout(() => setShowConfetti(false), 5000); // Remove o confetti após 5 segundos
        }
      }
    } catch (err) {
      setError('Erro ao processar sua solicitação. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // Componente de seleção de case
  const CaseSelection = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="grid grid-cols-1 md:grid-cols-2 gap-6"
    >
      {filteredCases.map(caseItem => (
        <motion.div
          key={caseItem.id}
          whileHover={{ scale: 1.02 }}
          className="cursor-pointer group relative"
          onClick={() => setSelectedCase(caseItem.id)}
        >
          <Card className="h-full bg-gradient-to-br from-[#FFFFFF]/10 to-transparent border-[0.8px] border-[#1D1D1B]/10 group-hover:border-[#FFD100] transition-all backdrop-blur-sm relative overflow-hidden">
            <CardContent className="p-6">
              <div className="absolute top-3 right-3 z-10 bg-[#FFD100] text-[#1D1D1B] text-xs font-medium py-1 px-2 rounded-full opacity-100 group-hover:opacity-100 transition-opacity">
                Clique para acessar
              </div>
              <div className="relative">
                <img
                  src={caseItem.thumbnail}
                  alt={caseItem.title}
                  className="w-full h-48 object-cover rounded-lg mb-4 group-hover:brightness-105 transition-all"
                />
              </div>
              <h3 className="text-xl font-semibold text-[#1D1D1B] mb-2">{caseItem.title}</h3>
              <p className="text-[#4D4D4D]">{caseItem.description}</p>
            </CardContent>
          </Card>
        </motion.div>
      ))}
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-white relative overflow-hidden">
      {showConfetti && (
        <ReactConfetti
          width={window.innerWidth}
          height={window.innerHeight}
          recycle={false}
          numberOfPieces={200}
          gravity={0.3}
          colors={['#FFD100', '#1D1D1B', '#4D4D4D']}
          onConfettiComplete={() => setShowConfetti(false)}
        />
      )}
      <div className="absolute inset-0">
        <FloatingPaths position={1} />
        <FloatingPaths position={-1} />
      </div>

      <div className="max-w-4xl mx-auto relative z-10 p-4 sm:p-6 lg:p-8">
        <motion.div
          className="flex justify-center items-center p-4"
          initial={{ scale: 0.9 }}
          animate={{ scale: 1 }}
        >
          <img
            src="/logo.png"
            alt="waffle"
            className="w-36 mb-4"
          />
        </motion.div>

        <div className="text-center mb-8">
          <motion.h1
            className="font-['Shrikhand'] text-4xl mb-4"
          >
            {pageTitle.split(" ").map((word, wordIndex) => (
              <span key={wordIndex} className="inline-block mr-2 last:mr-0">
                {word.split("").map((letter, letterIndex) => (
                  <motion.span
                    key={`${wordIndex}-${letterIndex}`}
                    initial={{ y: 100, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{
                      delay: wordIndex * 0.1 + letterIndex * 0.03,
                      type: "spring",
                      stiffness: 150,
                      damping: 25,
                    }}
                    className="inline-block text-transparent bg-clip-text 
                              bg-gradient-to-r from-[#1D1D1B] to-[#1D1D1B]/80"
                  >
                    {letter}
                  </motion.span>
                ))}
              </span>
            ))}
          </motion.h1>
          <p className="text-[#4D4D4D] text-base">
            insights reais dos nossos leitores que movem mentes e agitam mercados
          </p>
          {/* <p className="text-[#4D4D4D] text-sm mt-4">
            clique no card que desejar para saber mais
          </p> */}
        </div>

        {!selectedCase ? (
          <CaseSelection />
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="bg-gradient-to-br from-[#FFFFFF]/10 to-transparent rounded-xl p-6 mb-4 backdrop-blur-sm">
              <div className="flex items-start gap-4">
                {AVAILABLE_CASES
                  .filter(caseItem => caseItem.id === selectedCase)
                  .map(caseItem => (
                    <motion.div
                      key={caseItem.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="flex-1"
                    >
                      <div className="flex items-center gap-2 mb-3">
                        <div className="p-1.5 bg-[#FFD100] rounded-full">
                          <Download className="h-4 w-4 text-[#1D1D1B]" />
                        </div>
                        <h3 className="text-lg font-semibold text-[#1D1D1B]">
                          Case Selecionado
                        </h3>
                      </div>

                      <div className="bg-white rounded-lg p-3 border-[0.8px] border-[#1D1D1B]/10">
                        <div className="flex gap-3 items-center">
                          <img
                            src={caseItem.thumbnail}
                            alt={caseItem.title}
                            className="w-20 h-20 object-cover rounded-lg"
                          />
                          <div>
                            <h4 className="text-base font-bold text-[#1D1D1B] mb-1">
                              {caseItem.title}
                            </h4>
                            <p className="text-sm text-[#4D4D4D]">
                              {caseItem.description}
                            </p>
                          </div>
                        </div>
                      </div>

                      <button
                        onClick={() => setSelectedCase(null)}
                        className="mt-3 text-sm text-[#4D4D4D] hover:text-[#1D1D1B] transition-colors flex items-center gap-1"
                      >
                        <ArrowLeft className="w-3 h-3" />
                        Voltar para seleção de cases
                      </button>
                    </motion.div>
                  ))}
              </div>
            </div>

            <Card className="bg-white/95 border-[0.8px] border-[#1D1D1B]/10">
              <CardContent className="p-6">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <Input
                        placeholder="Nome"
                        value={formData.firstName}
                        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                        required
                        className="h-12 rounded-lg border-[0.8px] border-[#1D1D1B]/10 focus:border-[#1D1D1B]/30 focus:ring-0 bg-white"
                      />

                      <Input
                        placeholder="Sobrenome"
                        value={formData.lastName}
                        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                        required
                        className="h-12 rounded-lg border-[0.8px] border-[#1D1D1B]/10 focus:border-[#1D1D1B]/30 focus:ring-0 bg-white"
                      />
                    </div>

                    <Input
                      type="email"
                      placeholder="E-mail Corporativo"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="h-12 rounded-lg border-[0.8px] border-[#1D1D1B]/10 focus:border-[#1D1D1B]/30 focus:ring-0 bg-white"
                    />

                    <Input
                      placeholder="Empresa"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      required
                      className="h-12 rounded-lg border-[0.8px] border-[#1D1D1B]/10 focus:border-[#1D1D1B]/30 focus:ring-0 bg-white"
                    />

                    <select
                      value={formData.interesse}
                      onChange={(e) => setFormData({ ...formData, interesse: e.target.value })}
                      required
                      className="h-12 rounded-lg border-[0.8px] border-[#1D1D1B]/10 focus:border-[#1D1D1B]/30 focus:ring-0 bg-white w-full px-3"
                    >
                      <option value="">Gostaria de saber mais sobre nossas soluções para marcas?</option>
                      {INTERESS.map((interesse) => (
                        <option key={interesse} value={interesse}>{interesse}</option>
                      ))}
                    </select>

                    {selectedCase === 'webinar' && (
                      <>
                        <select
                          value={formData.cargo}
                          onChange={(e) => setFormData({ ...formData, cargo: e.target.value })}
                          required
                          className="h-12 rounded-lg border-[0.8px] border-[#1D1D1B]/10 focus:border-[#1D1D1B]/30 focus:ring-0 bg-white w-full px-3"
                        >
                          <option value="">Selecione seu cargo</option>
                          {CARGOS.map((cargo) => (
                            <option key={cargo} value={cargo}>{cargo}</option>
                          ))}
                        </select>

                        <select
                          value={formData.areaTrabalho}
                          onChange={(e) => setFormData({ ...formData, areaTrabalho: e.target.value })}
                          required
                          className="h-12 rounded-lg border-[0.8px] border-[#1D1D1B]/10 focus:border-[#1D1D1B]/30 focus:ring-0 bg-white w-full px-3"
                        >
                          <option value="">Você trabalha em</option>
                          {AREAS_TRABALHO.map((area) => (
                            <option key={area} value={area}>{area}</option>
                          ))}
                        </select>

                        <select
                          value={formData.areaEmpresa}
                          onChange={(e) => setFormData({ ...formData, areaEmpresa: e.target.value })}
                          required
                          className="h-12 rounded-lg border-[0.8px] border-[#1D1D1B]/10 focus:border-[#1D1D1B]/30 focus:ring-0 bg-white w-full px-3"
                        >
                          <option value="">Área da empresa</option>
                          {AREAS_EMPRESA.map((area) => (
                            <option key={area} value={area}>{area}</option>
                          ))}
                        </select>

                        <select
                          value={formData.interesse}
                          onChange={(e) => setFormData({ ...formData, interesse: e.target.value })}
                          required
                          className="h-12 rounded-lg border-[0.8px] border-[#1D1D1B]/10 focus:border-[#1D1D1B]/30 focus:ring-0 bg-white w-full px-3"
                        >
                          <option value="">Gostaria de saber mais sobre nossas soluções para marcas?</option>
                          {INTERESS.map((interesse) => (
                            <option key={interesse} value={interesse}>{interesse}</option>
                          ))}
                        </select>

                      </>
                    )}
                  </div>

                  {error && (
                    <div className="text-[#1D1D1B] text-sm text-center">{error}</div>
                  )}

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full h-12 bg-[#1D1D1B] hover:opacity-90 text-white rounded-lg font-bold transform hover:scale-[1.02] transition-all duration-300 shadow-lg"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-2 text-white">
                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="text-white">processando...</span>
                      </span>
                    ) : (
                      <span className="text-white">
                        {selectedCase === 'webinar' ? 'Inscrever-se no Webinar' : 'Liberar Acesso ao Case'}
                      </span>
                    )}
                  </Button>

                  <div className="mt-4 flex items-start gap-2">
                    <input
                      type="checkbox"
                      id="trendReport"
                      checked={formData.wantsTrendReport}
                      onChange={(e) => setFormData({ ...formData, wantsTrendReport: e.target.checked })}
                      className="mt-1"
                    />
                    <label htmlFor="trendReport" className="text-sm text-[#4D4D4D]">
                      Você quer receber o trend report? A nova newsletter de marketing do grupo waffle, com materiais como esse.
                    </label>
                  </div>
                </form>
                <div className="mt-4 text-center text-xs text-[#4D4D4D]">
                  {selectedCase === 'webinar' && (
                    <p className="mb-2">
                      Você receberá um e-mail com o link do webinar próximo à data do evento.
                    </p>
                  )}
                  <p>
                    Quer ver como comunicaríamos sua marca na waffle?
                    <a
                      href="https://thenewscc.typeform.com/to/twCcjRbQ"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline font-semibold"
                    >
                      Clique aqui.
                    </a>
                  </p>
                </div>
              </CardContent>

            </Card>
          </motion.div>
        )}

        <div className="mt-6 text-center">
          <p className="text-[#4D4D4D] text-xs font-['Shrikhand']">
            feito por <span className="text-[#1D1D1B]">the news</span> • 2025
          </p>
        </div>
      </div>
    </div>
  );
}